import { ResizeMode, Video } from 'expo-av';
import React, { useState } from 'react';
import {
  ActivityIndicator,
  Image,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import theme from '../theme';
import { defaultScale } from '../utils/scaling';
import { useGetVideoUrl } from './Hooks/useGetVideoUrl';
import { usePauseVideoOnNavigation } from './Hooks/usePauseVideoOnNavigation';
import { useShowCustomPlayButton } from './Hooks/useShowCustomPlayButton';
import VideoPlayBackButton from './VideoPlayBackButton';

interface IProps {
  navigation: any;
  t(key: string): string;
}

const StartAssessment: React.FC<IProps> = ({ t, navigation }) => {
  const videoRef = React.useRef<any>(null);
  const videoUrl = useGetVideoUrl('benchTutorial');
  usePauseVideoOnNavigation(navigation, videoRef);
  const {
    onPlayButtonPressed,
    onVideoFinishedPlaying,
    shouldNativeControlsBeVisible,
    showPlayButton,
  } = useShowCustomPlayButton(videoRef);
  const [isVideoVisible, setVideoVisible] = useState(false);
  const [isVideoLoading, setIsVideoLoading] = useState(false);
  const handleVideoTextClicked = () => {
    setVideoVisible(true);
  };
  const showVideo = isVideoVisible && !!videoUrl;
  const showVideoLoading =
    (isVideoLoading && isVideoVisible) || (isVideoVisible && !videoUrl);
  const isPlayButtonVisible =
    showPlayButton && !showVideoLoading && isVideoVisible;
  const handlePlaybackStatusUpdate = async (playbackStatus) => {
    if (playbackStatus.didJustFinish) {
      if (videoRef.current) {
        await videoRef.current.setPositionAsync(0);
        await videoRef.current.pauseAsync();
        onVideoFinishedPlaying();
      }
    }
  };

  return (
    <View style={styles.container}>
      <View
        style={[
          styles.imageContainer,
          { backgroundColor: isVideoVisible ? theme.BLACK : theme.WHITE },
        ]}
      >
        {!isVideoVisible && (
          <Image
            source={require('../assets/images/bench-press-with-uni-bands-full.png')}
            style={styles.image}
            resizeMode='contain'
          />
        )}
        {showVideo && (
          <Video
            ref={videoRef}
            source={{
              uri: videoUrl,
            }}
            resizeMode={ResizeMode.CONTAIN}
            style={styles.image}
            onLoad={() => {
              setIsVideoLoading(false);
            }}
            onLoadStart={() => {
              setIsVideoLoading(true);
            }}
            useNativeControls={shouldNativeControlsBeVisible}
            onPlaybackStatusUpdate={handlePlaybackStatusUpdate}
          />
        )}
        {isPlayButtonVisible && (
          <VideoPlayBackButton onPress={onPlayButtonPressed} />
        )}
        {showVideoLoading && (
          <View style={styles.loadingContainer}>
            <ActivityIndicator
              size='small'
              color={theme.PRIMARY}
              animating={true}
            />
          </View>
        )}
      </View>
      <View style={styles.bulletPoints}>
        <View style={styles.bulletPointContainer}>
          <View style={styles.bulletPoint} />
          <Text>
            <Text style={styles.regBulletText}>
              {t('startStrengthAssessmentBulletOne_1')}
            </Text>
            <Text style={styles.boldBulletText}>
              {t('startStrengthAssessmentBulletOne_2')}
            </Text>
            <Text style={styles.regBulletText}>
              {t('startStrengthAssessmentBulletOne_3')}
            </Text>
            <Text style={styles.boldBulletText}>
              {t('startStrengthAssessmentBulletOne_4')}
            </Text>
          </Text>
        </View>
        <View style={styles.bulletPointContainer}>
          <View style={styles.bulletPoint} />
          <TouchableOpacity
            onPress={handleVideoTextClicked}
            style={{ flexDirection: 'row', flexWrap: 'wrap' }}
          >
            <Text style={styles.regBulletText}>
              {t('startStrengthAssessmentBulletTwo_1')}
            </Text>
            <Text style={[styles.boldBulletText, { color: theme.PRIMARY }]}>
              {t('startStrengthAssessmentBulletTwo_2')}
            </Text>
            <Text style={styles.regBulletText}>
              {t('startStrengthAssessmentBulletTwo_3')}
            </Text>
          </TouchableOpacity>
        </View>
      </View>
    </View>
  );
};

export default StartAssessment;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  imageContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  regBulletText: { ...theme.textStyle.bodyCopyReg },
  boldBulletText: { ...theme.textStyle.bodyCopyBold },
  bulletPoint: {
    height: defaultScale(5),
    width: defaultScale(5),
    borderRadius: defaultScale(2.5),
    backgroundColor: theme.BLACK,
    margin: defaultScale(10),
  },
  bulletPointContainer: {
    flexDirection: 'row',
    marginBottom: defaultScale(10),
    maxWidth: '90%',
  },
  loadingContainer: {
    ...StyleSheet.absoluteFillObject,
    justifyContent: 'center',
    alignItems: 'center',
  },
  bulletPoints: {
    marginVertical: defaultScale(15),
  },
});
